import * as React from "react";
import { alpha, styled } from "@mui/material/styles";
import IconButton from "@mui/material/IconButton";
import {
  MoreVert as MoreVertIcon,
  Edit as EditIcon,
  Archive as ArchiveIcon,
} from "@mui/icons-material";
import {
  CircularProgress,
  Menu,
  MenuItem,
  MenuProps,
  Tooltip,
} from "@mui/material";
import Divider from "@mui/material/Divider";
import { useConfig } from "../containers/ConfigContext";

const StyledMenu = styled((props: MenuProps) => (
  <Menu
    elevation={0}
    anchorOrigin={{
      vertical: "bottom",
      horizontal: "right",
    }}
    transformOrigin={{
      vertical: "top",
      horizontal: "right",
    }}
    {...props}
  />
))(({ theme }) => ({
  "& .MuiPaper-root": {
    borderRadius: 6,
    marginTop: theme.spacing(1),
    minWidth: 180,
    color:
      theme.palette.mode === "light"
        ? "rgb(55, 65, 81)"
        : theme.palette.grey[300],
    boxShadow:
      "rgb(255, 255, 255) 0px 0px 0px 0px, rgba(0, 0, 0, 0.05) 0px 0px 0px 1px, rgba(0, 0, 0, 0.1) 0px 10px 15px -3px, rgba(0, 0, 0, 0.05) 0px 4px 6px -2px",
    "& .MuiMenu-list": {
      padding: "4px 0",
    },
    "& .MuiMenuItem-root": {
      "& .MuiSvgIcon-root": {
        fontSize: 18,
        marginRight: theme.spacing(1.5),
      },
      "&.error": {
        color: theme.palette.error.main,
      },
      "&:active": {
        backgroundColor: alpha(
          theme.palette.primary.main,
          theme.palette.action.selectedOpacity,
        ),
      },
    },
  },
}));

export type ActionItem = {
  label: React.ReactNode;
  icon?: React.ReactNode;
  tooltip?: React.ReactNode;
  onClick?: () => Promise<void> | void;
};

interface TableAction {
  onEdit?: () => void;
  onDelete?: () => void;
  additionalActions?: ActionItem[];
}

export default function TableActions(props: TableAction) {
  const { additionalActions } = props;
  const [actionLoadings, setActionLoadings] = React.useState<boolean[]>(
    additionalActions?.map(() => false) ?? [],
  );
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const { localized } = useConfig();
  const open = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const renderAdditionalActions = () => {
    return (additionalActions ?? []).map((action, index) => {
      const handleClick = async () => {
        if (action.onClick) {
          try {
            setActionLoadings((prev) => {
              const newLoadings = [...prev];
              newLoadings[index] = true;
              return newLoadings;
            });
            await action.onClick();
          } catch (error) {
            console.error(error);
          } finally {
            setActionLoadings((prev) => {
              const newLoadings = [...prev];
              newLoadings[index] = false;
              return newLoadings;
            });
          }
        }
        handleClose();
      };

      const element = (
        <MenuItem key={index} onClick={handleClick}>
          {actionLoadings[index] ? (
            <CircularProgress
              size="18px"
              sx={(theme) => ({
                marginRight: theme.spacing(1.5),
              })}
            />
          ) : (
            action.icon
          )}
          {action.label}
        </MenuItem>
      );

      if (action.tooltip) {
        return (
          <Tooltip title={action.tooltip} key={index} placement="right">
            {element}
          </Tooltip>
        );
      }
      return element;
    });
  };

  const noAdditionalActions =
    !additionalActions || additionalActions.length === 0;
  const btnCount =
    props.onEdit && props.onDelete
      ? 2
      : !props.onEdit && !props.onDelete
        ? 0
        : 1;
  if (btnCount === 0 && noAdditionalActions) {
    return <div></div>;
  }
  if (btnCount === 1 && noAdditionalActions) {
    return props.onEdit ? (
      <MenuItem
        onClick={() => {
          if (props.onEdit) {
            props.onEdit();
          }
        }}
        data-testid={"Edit"}
      >
        <EditIcon />
        {localized("Edit")}
      </MenuItem>
    ) : (
      <MenuItem
        className="error"
        onClick={() => {
          if (props.onDelete) {
            props.onDelete();
          }
        }}
        data-testid={"Delete"}
      >
        <ArchiveIcon color="error" />
        {localized("Delete")}
      </MenuItem>
    );
  }

  return (
    <div>
      <IconButton onClick={handleClick} data-testid={"Actions"}>
        <MoreVertIcon />
      </IconButton>
      <StyledMenu anchorEl={anchorEl} open={open} onClose={handleClose}>
        {renderAdditionalActions()}
        {props.onEdit && (
          <MenuItem
            onClick={() => {
              if (props.onEdit) {
                props.onEdit();
              }
              handleClose();
            }}
            data-testid={"Edit"}
          >
            <EditIcon />
            {localized("Edit")}
          </MenuItem>
        )}
        <Divider sx={{ my: 0.5 }} />
        {props.onDelete && (
          <MenuItem
            className="error"
            onClick={() => {
              if (props.onDelete) {
                props.onDelete();
              }
              handleClose();
            }}
            data-testid={"Delete"}
          >
            <ArchiveIcon color="error" />
            {localized("Delete")}
          </MenuItem>
        )}
      </StyledMenu>
    </div>
  );
}
